import { useCallback, useMemo } from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { layoutsForAllCasesState, DEFAULT_LAYOUT, updateCaseLayouts } from './viewerLoaderState';
import type { Layout, Layouts } from './viewerLoaderState';
import { useViewerMetadata } from './useViewerMetadata';
import { useHangingProtocol } from 'domains/viewer/HangingProtocol/useHangingProtocol';
import { EMPTY } from 'config/constants';
import pick from 'ramda/es/pick';
import { useCurrentCaseId } from 'hooks/useCurrentCase';
import { isPreviewingHangingProtocolState } from '../../HangingProtocol/state';
import { WithApolloContext } from 'modules/Apollo/types';

type UseLayouts = {
  layoutForWindow: Nullable<Layout>;
  layoutsForCase: Layouts;
  setLayoutsForCase: (arg1: Layouts) => void;
  setLayoutForWindow: (arg1: Layout) => void;
  loading: boolean;
};

export const useLayouts = ({ context }: WithApolloContext): UseLayouts => {
  const [allLayouts, setAllLayouts] = useRecoilState(layoutsForAllCasesState);
  const isPreviewingHangingProtocol = useRecoilValue(isPreviewingHangingProtocolState);
  const { openWindowsIds, windowId } = useViewerMetadata();
  const currentCaseId = useCurrentCaseId();
  const { hangingProtocolLayouts, loadingHangingProtocol } = useHangingProtocol({ context });

  const currentLayoutsForCase: Layouts =
    !isPreviewingHangingProtocol && currentCaseId != null
      ? allLayouts[currentCaseId]
      : EMPTY.OBJECT;

  const setLayoutsForCase = useCallback(
    (updatedLayouts: Layouts) => {
      if (currentCaseId == null) return;

      setAllLayouts((currentAllLayouts) =>
        updateCaseLayouts(currentAllLayouts, currentCaseId, updatedLayouts)
      );
    },
    [currentCaseId, setAllLayouts]
  );

  const setLayoutForWindow = useCallback(
    (layout: Layout) => {
      if (currentCaseId == null || windowId == null) return;

      setAllLayouts((currentAllLayouts) =>
        updateCaseLayouts(currentAllLayouts, currentCaseId, {
          ...currentAllLayouts[currentCaseId],
          [windowId]: layout,
        })
      );
    },
    [currentCaseId, setAllLayouts, windowId]
  );

  // Filter out layouts for any windows which are not currently open.
  // This lets us 'save' the previously used layout and also give an accurate current layout.
  //
  // The window this viewer is rendering in always counts as open, whatever openWindowsIds says.
  // That list comes from the Workspace Manager extension, and there is a reachable state where it
  // comes back without this window: the extension reports windows whose names don't map to a window
  // id, so computeRawOpenTabsInfo's loop yields nothing and the artificial-tab fallback never fires
  // (it only covers there being no extension state at all). Without this, `pick` dropped the current
  // window, layoutForWindow came back undefined, and useRenderedViewportIdsForWindow's
  // `layout ?? [0, 0]` painted an empty reading area -- no error, no log, every thumbnail loaded and
  // not one viewport (VX-4764).
  const visibleWindowIds: ReadonlyArray<string> = useMemo(
    () =>
      windowId != null && !openWindowsIds.includes(windowId)
        ? [...openWindowsIds, windowId]
        : openWindowsIds,
    [openWindowsIds, windowId]
  );

  const layoutsForCase: Layouts = useMemo(() => {
    const defaultLayouts: Layouts = Object.fromEntries(
      visibleWindowIds.map((windowId) => [windowId, DEFAULT_LAYOUT])
    );

    return pick(visibleWindowIds, {
      ...defaultLayouts,
      ...hangingProtocolLayouts,
      ...currentLayoutsForCase,
    });
  }, [currentLayoutsForCase, hangingProtocolLayouts, visibleWindowIds]);

  // @ts-expect-error [EN-10263] - TS2538 - Type 'null' cannot be used as an index type. | TS2538 - Type 'undefined' cannot be used as an index type.
  const layoutForWindow = layoutsForCase[windowId];

  return {
    layoutForWindow,
    setLayoutForWindow,
    layoutsForCase,
    setLayoutsForCase,
    loading: loadingHangingProtocol,
  };
};
